import { ChakraProvider, ColorMode } from "@chakra-ui/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { PropsWithChildren, StrictMode } from "react";
import { IntlProvider } from "react-intl";

import { Fonts, NetworkProvider, getRanaTheme, ranaToastOptions } from "@nens/ranalib";
import { FramedScreenshotProvider } from "@nens/react/components";
import { LocaleProvider } from "@nens/react/contexts";
import { useLocalStorage } from "@nens/react/hooks";
import { DEFAULT_QUERY_OPTIONS } from "@nens/react-query-utils";
import { SentryErrorBoundary, initSentry } from "@nens/sentry";

import { ProgressDialogStackProvider } from "@/components/progress-dialog";
import { StatusBannerStack } from "@/components/status-banner";
import { FileUploadingProvider } from "@/features/project-file-uploading";
import { defaultLocale, supportedLocales, translations } from "@/translations";
import { isTestEnvironment } from "@/utils/is-test-environment";

import { AppRoutes } from "./routes/routes";
import { E2EStatusBanner } from "./toasts-and-banners/e2e-status";
import { FeatureFlagsBanner } from "./toasts-and-banners/feature-flags";
import { ShowToastOnLocaleChange } from "./toasts-and-banners/show-toast-on-local-change";

initSentry({
  // Rana has its own Sentry projects, rana-frontend and rana-frontend-not-production
  keys: {
    production:
      "https://c4fa962d56dd34efb90ff1db3233a210@o80571.ingest.us.sentry.io/4511502299758592",
    staging: "https://f327b56fdf70fc56856f573d7614b3b9@o80571.ingest.us.sentry.io/4511502310637568",
    // Usually sending to Sentry from development is overkill, but if you need it, copy the key from
    // staging to here.
    development: null,
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    // Other apps need updates every now and then. We don't
    queries: { ...DEFAULT_QUERY_OPTIONS, staleTime: undefined },
  },
});

function WithIntl({ children }: PropsWithChildren) {
  // FUTURE: Use locale from context to support multiple languages.
  // const { locale } = useContext(LocaleContext);
  const locale = "en";

  return (
    <IntlProvider
      key={locale}
      locale={locale}
      defaultLocale={defaultLocale}
      messages={translations[locale]}
    >
      {children}
    </IntlProvider>
  );
}

export function App() {
  const userColorModePreference =
    window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  const [colorMode, setColorMode] = useLocalStorage<ColorMode>(
    "color-mode-preference",
    userColorModePreference
  );

  document.documentElement.setAttribute("data-theme", colorMode);

  return (
    <StrictMode>
      <SentryErrorBoundary appName="rana" theme="rana">
        <ChakraProvider
          toastOptions={ranaToastOptions}
          theme={getRanaTheme(colorMode)}
          colorModeManager={{
            type: "localStorage",
            get: () => colorMode,
            set: (value) => setColorMode(value as ColorMode),
          }}
        >
          <Fonts />
          <QueryClientProvider client={queryClient}>
            <LocaleProvider supportedLocales={supportedLocales} defaultLocale={defaultLocale}>
              <WithIntl>
                <NetworkProvider>
                  <ShowToastOnLocaleChange />
                  {isTestEnvironment() && (
                    <StatusBannerStack>
                      <E2EStatusBanner />
                      <FeatureFlagsBanner />
                    </StatusBannerStack>
                  )}
                  <ProgressDialogStackProvider>
                    <FramedScreenshotProvider
                      cancelButtonProps={{ variant: "secondary" }}
                      confirmButtonProps={{ variant: "primary" }}
                    >
                      <FileUploadingProvider>
                        <AppRoutes />
                      </FileUploadingProvider>
                    </FramedScreenshotProvider>
                  </ProgressDialogStackProvider>
                </NetworkProvider>
              </WithIntl>
            </LocaleProvider>
          </QueryClientProvider>
        </ChakraProvider>
      </SentryErrorBoundary>
    </StrictMode>
  );
}
